// src/pages/shop/SaleReturnIndex.js
import React, { useState, useEffect } from "react";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Collapse,
  IconButton,
  Pagination,
  MenuItem,
  FormControl,
  Select,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Search as SearchIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";
import Skeleton from "@mui/material/Skeleton";
import ErrorPage from "../Components/ErrorPage";
import ShopAdd from "./saleReturnAdd";
import useDelete from "../../api/useDelete";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { printSaleReturn } from "../utils/printSaleReturn";

const SaleReturnIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRow, setExpandedRow] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [notification, setNotification] = useState({ open: false, message: "", severity: "success" });

  // dialogs
  const [openAdd, setOpenAdd] = useState(false);

  const {
    data,
    isLoading,
    isError,
    refetch,
  } = useFetchObjects(
    ["sale-return", searchTerm, page, rowsPerPage],
    `shop/sale-returns/?search=${searchTerm}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  useEffect(() => {
    refetch();
  }, [searchTerm, page, rowsPerPage, refetch]);

  const { handleDelete, ConfirmDialog } = useDelete("shop/sale-returns", token);

  const handleDeleteClick = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/shop/sale-returns/${deleteDialog.id}/`,
        { headers: { Authorization: `Token ${token}` } }
      );
      setNotification({ open: true, message: "Sale return deleted and stock updated!", severity: "success" });
      refetch();
    } catch (error) {
      setNotification({ open: true, message: "Failed to delete sale return", severity: "error" });
    }
    setDeleteDialog({ open: false, id: null });
  };

  const saleReturns = data && data.results ? data.results : data || [];
  const totalCount = data && data.count ? data.count : saleReturns.length;

  const toggleExpandRow = (id) =>
    setExpandedRow((prev) => (prev === id ? null : id));

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  if (isError) {
    return <ErrorPage message={t("saleReturn.failedToLoad")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* ── header ─────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">{t("SaleReturnIndex.title")}</Typography>

        <Link to="/shop/sale-return-add">
          <Button variant="contained" color="primary">
            {t("SaleReturnIndex.addbtn")}
          </Button>
        </Link>
      </Box>

      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("shop.dataLoadedOn")} {new Date().toLocaleString()}
      </Typography>

      {/* ── search ─────────────────────────────────────────── */}
      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("SaleReturnIndex.search")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* ── table ──────────────────────────────────────────── */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table sx={{ minWidth: 650 }} aria-label="sale return table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }} />
              <TableCell sx={{ color: "white" }}>
                {t("SaleReturnIndex.id")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("SaleReturnIndex.shop")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("sale.customer", "Customer")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("SaleReturnIndex.SaleID")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("SaleReturnIndex.totalRefound")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("SaleReturnIndex.date")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("SaleReturnIndex.reason")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading
              ? Array.from({ length: 5 }).map((_, r) => (
                  <TableRow key={`sk-${r}`}>
                    {Array.from({ length: 9 }).map((_, c) => (
                      <TableCell key={`sk-${r}-${c}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : saleReturns.map((saleReturn, index) => {
                  const totalRefund = saleReturn.return_items
                    .reduce((sum, item) => {
                      const val =
                        item.calculated_refund_amount?.parsedValue ??
                        parseFloat(item.refund_amount) ??
                        0;
                      return sum + val;
                    }, 0)
                    .toFixed(2);

                  const saleId = saleReturn.sale || saleReturn.sale_details?.id || 'N/A';

                  return (
                    <React.Fragment key={saleReturn.id}>
                      {/* summary row */}
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleExpandRow(saleReturn.id)}
                          >
                            {expandedRow === saleReturn.id ? (
                              <CollapseIcon />
                            ) : (
                              <ExpandIcon />
                            )}
                          </IconButton>
                        </TableCell>

                        <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                        <TableCell>{saleReturn.shop_details?.name}</TableCell>
                        <TableCell>
                          {saleReturn.sale_details?.customer_details?.name || "Walk-in Customer"}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`Sale - #${saleId}`} 
                            color="primary" 
                            size="small" 
                            variant="outlined"
                            component={Link}
                            to={`/shop/sales`}
                            clickable
                          />
                        </TableCell>
                        <TableCell>
                          {saleReturn.sale_details?.currency_details?.title || ""}{" "}
                          {totalRefund}
                        </TableCell>
                        <TableCell>
                          {new Date(saleReturn.return_date).toLocaleString()}
                        </TableCell>
                        <TableCell>{saleReturn.reason}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => printSaleReturn(saleReturn)}
                            color="primary"
                            sx={{ mr: 1 }}
                          >
                            <PrintIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(saleReturn.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>

                      {/* expanded detail row */}
                      <TableRow>
                        <TableCell
                          style={{ paddingBottom: 0, paddingTop: 0 }}
                          colSpan={9}
                        >
                          <Collapse
                            in={expandedRow === saleReturn.id}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ m: 1 }}>
                              <Typography
                                variant="subtitle2"
                                gutterBottom
                                component="div"
                              >
                                {t("SaleReturnIndex.retrunItems")} (
                                {saleReturn.return_items.length})
                              </Typography>

                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>
                                      {t("SaleReturnIndex.product")}
                                    </TableCell>
                                    <TableCell align="right">
                                      {t("SaleReturnIndex.salePrice")}
                                    </TableCell>
                                    <TableCell align="right">
                                      {t("SaleReturnIndex.qty")}
                                    </TableCell>
                                    <TableCell align="right">
                                      {t("SaleReturnIndex.refound")}
                                    </TableCell>
                                    <TableCell>
                                      {t("SaleReturnIndex.date")}
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {saleReturn.return_items.map((item) => {
                                    const itemRefund =
                                      item.calculated_refund_amount
                                        ?.parsedValue ??
                                      parseFloat(item.refund_amount) ??
                                      0;

                                    return (
                                      <TableRow key={item.id}>
                                        <TableCell>
                                          {
                                            item.sale_item_details?.product
                                              ?.name
                                          }
                                        </TableCell>
                                        <TableCell align="right">
                                          {item.sale_item_details?.sale_price}
                                        </TableCell>
                                        <TableCell align="right">
                                          {item.return_quantity}
                                        </TableCell>
                                        <TableCell align="right">
                                          {itemRefund.toFixed(2)}
                                        </TableCell>
                                        <TableCell>
                                          {new Date(
                                            item.created_at
                                          ).toLocaleString()}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="10px"
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>{t("saleReturn.rowsPerPage")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
            size="small"
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
          sx={{ ml: 2 }}
        />
      </Grid>

      {/* dialogs */}
      {openAdd && (
        <ShopAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
          owner={auth?.user?.user?.id}
        />
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon color="error" />
            Confirm Delete
          </Box>
        </DialogTitle>
        <DialogContent>
          Are you sure you want to delete this sale return? The returned quantities will be removed from stock.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
      
      <ConfirmDialog />
    </Box>
  );
};

export default SaleReturnIndex;
